import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, filter, switchMap, takeUntil } from 'rxjs/operators';
import { LaftStatus } from 'src/app/shared/enums/LaftStatus.enum';
import { OnboardingService } from 'src/app/shared/services/onboarding.service';
import { ContractService } from 'src/app/shared/services/contract.service';
import { SocketService } from 'src/app/shared/services/socket.service';

@Component({
  selector: 'app-onboarding-banner',
  templateUrl: './onboarding-banner.component.html',
  styleUrls: ['./onboarding-banner.component.scss'],
})
export class OnboardingBannerComponent implements OnInit, OnDestroy {
  @Input() userId: string;
  @Input() latfStatus: string = LaftStatus.Pending;

  signed = false;
  uploadedInvoice = false;
  hasOnboardingData = false;
  contracts: any[] = [];

  @Output() uploadClick = new EventEmitter<MouseEvent>();
  @Output() signClick = new EventEmitter<MouseEvent>();
  @Output() hasData = new EventEmitter<boolean>();
  @Output() isFullyCompleted = new EventEmitter<boolean>();

  private destroy$ = new Subject<void>();
  private reloadOnboarding$ = new Subject<void>();

  constructor(
    private onboardingService: OnboardingService,
    private socketService: SocketService,
    private contractService: ContractService,
  ) {}

  onUploadBannerClick(event: MouseEvent): void {
    this.uploadClick.emit(event);
  }

  onSignDocumentsBannerClick(event: any): void {
    this.signClick.emit(event);
  }

  get onboardingHeaderTitle(): string {
    const steps = !this.uploadedInvoice ? '2 pasos' : '1 paso';
    return `Estás a ${steps} de financiar tu primera factura`;
  }

  get onboardingHeaderSubtitle(): string {
    return 'Comienza a recibir liquidez de forma rápida y segura.';
  }

  get hasContractReadyToSign(): boolean {
    return (
      this.latfStatus === LaftStatus.Completed &&
      (this.contracts ?? []).some((c) => c?.status === 'signature requested')
    );
  }

  ngOnInit(): void {
    if (!this.userId) return;

    this.reloadOnboarding$
      .pipe(
        switchMap(() =>
          forkJoin({
            onboarding: this.onboardingService.getOnboarding(this.userId).pipe(
              catchError(() => {
                this.hasData.emit(false);
                return of(null);
              }),
            ),
            contracts:
              typeof this.contractService?.getMyContracts === 'function'
                ? this.contractService.getMyContracts().pipe(catchError(() => of([])))
                : of([]),
          }),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(({ onboarding, contracts }) => {
        if (!onboarding) return;
        this.contracts = Array.isArray(contracts) ? contracts : [];
        this.applyOnboardingData(onboarding);
      });

    this.socketService.socketEvent
      .pipe(
        takeUntil(this.destroy$),
        filter(
          (event: any) =>
            event?.type === 'laft_status' ||
            event?.type === 'contract_status' ||
            event?.type === 'reloadLaftData',
        )
      )
      .subscribe(() => {
        this.reloadOnboarding$.next();
      });

    this.reloadOnboarding$.next();
  }

  ngOnDestroy(): void {
    this.hasData.emit(false);
    this.destroy$.next();
    this.destroy$.complete();
    this.reloadOnboarding$.complete();
  }

  private applyOnboardingData(data: any): void {
    const signed = data.milestones?.contract?.signed;
    const uploadedInvoice = data.milestones?.invoice?.uploadedInvoice;
    const latfStatusRaw = data.milestones?.laft?.status;
    const hasLaftMilestoneStatus = typeof latfStatusRaw === 'string' && latfStatusRaw.trim() !== '';

    if (typeof signed === 'boolean') this.signed = signed;
    if (typeof uploadedInvoice === 'boolean') this.uploadedInvoice = uploadedInvoice;
    if (hasLaftMilestoneStatus) {
      this.latfStatus = latfStatusRaw;
    }

    this.hasOnboardingData = true;
    const isFullyCompleted =
      (this.latfStatus ?? '').trim().toLowerCase() === LaftStatus.Completed.toLowerCase() &&
      this.uploadedInvoice === true &&
      this.signed === true;
    this.isFullyCompleted.emit(isFullyCompleted);

    this.hasData.emit(true);
  }
}
